import { QuizzesService } from "@/client"
import type { ProgramTag } from "@/client/types.gen"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { emptyStateCopy } from "@/components/Common/emptyStateCopy"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { QuizFilterControls } from "@/components/Quizzes/QuizFilterControls"
import { Tooltip } from "@/components/ui/tooltip"
import { useStudentLabels } from "@/hooks/useStudentLabels"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Badge, Box, Flex, Stack, Table, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router"
import { useEffect, useMemo, useState } from "react"

export const Route = createFileRoute("/_layout/quizzes/")({
  component: QuizzesPage,
})

interface SubjectInfo {
  id: string
  name: string
}

interface QuizAvailable {
  id: string
  name: string
  time_limit_minutes: number | null
  question_count: number
  allow_retake: boolean
  cutoff_date: string | null
  subjects: SubjectInfo[]
  tags: string[]
  programs: ProgramTag[]
  attempt_count: number
  last_attempt_date: string | null
}

function QuizzesPage() {
  const { quizzesLabel } = useStudentLabels()
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [selectedSubjects, setSelectedSubjects] = useState<SubjectInfo[]>([])
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput)
    }, 1000)

    return () => clearTimeout(timer)
  }, [searchInput])

  const { data, isLoading, error } = useQuery({
    queryKey: [
      "availableQuizzes",
      debouncedSearch,
      selectedSubjects,
      selectedTags,
    ],
    queryFn: () =>
      QuizzesService.listAvailableQuizzes({
        search: debouncedSearch || undefined,
        subjectIds:
          selectedSubjects.length > 0
            ? selectedSubjects.map((s) => s.id)
            : undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
      }),
    staleTime: 30_000,
  })

  const { data: unfilteredQuizData } = useQuery({
    queryKey: ["availableQuizzes", "subject-options"],
    queryFn: () => QuizzesService.listAvailableQuizzes({ limit: 200 }),
    staleTime: 60_000,
  })

  const availableSubjects = useMemo(() => {
    const subjects = new Map<string, SubjectInfo>()
    for (const quiz of unfilteredQuizData?.data ?? []) {
      for (const subject of quiz.subjects ?? []) {
        subjects.set(subject.id, subject)
      }
    }
    return [...subjects.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [unfilteredQuizData?.data])

  const breadcrumbs = useRouteBreadcrumbs("quizzes.index")

  if (isLoading) {
    return (
      <PageShell title={quizzesLabel} breadcrumbs={breadcrumbs}>
        <Box data-testid="quizzes-loading">
          <PageSkeleton variant="table" rows={4} />
        </Box>
      </PageShell>
    )
  }

  if (error) {
    return (
      <PageShell title={quizzesLabel} breadcrumbs={breadcrumbs}>
        <Box
          p={6}
          borderRadius="md"
          bg="red.50"
          borderWidth={1}
          borderColor="red.200"
        >
          <Text color="red.700">
            Error loading available tests. Please try again.
          </Text>
        </Box>
      </PageShell>
    )
  }

  const quizzes = (data?.data || []) as QuizAvailable[]
  const hasActiveFilters =
    debouncedSearch.trim().length > 0 || selectedSubjects.length > 0
  const emptyCopy = hasActiveFilters
    ? emptyStateCopy.quizzes.filtered
    : emptyStateCopy.quizzes.available

  return (
    <PageShell
      title={quizzesLabel}
      description="Select a test below to begin. Make sure you have enough time to complete the test before the cutoff date."
      breadcrumbs={breadcrumbs}
    >
      <Stack gap={6}>
        <QuizFilterControls
          search={searchInput}
          onSearchChange={setSearchInput}
          selectedSubjects={selectedSubjects}
          onSubjectsChange={setSelectedSubjects}
          availableSubjects={availableSubjects}
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
        />

        {/* Quiz Table */}
        {quizzes.length === 0 ? (
          <AppEmptyState
            title={emptyCopy.title}
            description={emptyCopy.description}
            data-testid="quizzes-empty-state"
          />
        ) : (
          <>
            <Box
              borderWidth={1}
              borderRadius="md"
              overflow="hidden"
              display={{ base: "none", md: "block" }}
              data-testid="quizzes-table"
            >
              <Table.Root size="lg" interactive>
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader minW="320px">
                      Test Name
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Questions
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Time Limit
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Due Date
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Attempts
                    </Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="center">
                      Retakes
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {quizzes.map((quiz) => (
                    <Table.Row
                      key={quiz.id}
                      onClick={() =>
                        navigate({
                          to: "/quizzes/$quizId",
                          params: { quizId: quiz.id },
                        })
                      }
                      cursor="pointer"
                      borderLeftWidth={
                        quiz.attempt_count === 0 ? "4px" : undefined
                      }
                      borderLeftColor={
                        quiz.attempt_count === 0 ? "orange.400" : undefined
                      }
                    >
                      <Table.Cell>
                        <Link
                          to={`/quizzes/${quiz.id}`}
                          style={{ textDecoration: "none" }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Text fontWeight="medium" color="blue.600">
                            {quiz.name}
                          </Text>
                          <Flex gap={2} wrap="wrap" mt={2}>
                            {quiz.subjects.map((subject) => (
                              <Badge
                                key={subject.id}
                                colorPalette="purple"
                                size="sm"
                              >
                                {subject.name}
                              </Badge>
                            ))}
                            {(quiz.tags ?? []).map((tag) => (
                              <Badge key={tag} colorPalette="blue" size="sm">
                                {tag}
                              </Badge>
                            ))}
                          </Flex>
                        </Link>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge colorPalette="blue">{quiz.question_count}</Badge>
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {quiz.time_limit_minutes ? (
                          <Badge colorPalette="orange">
                            {quiz.time_limit_minutes} min
                          </Badge>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            No limit
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {quiz.cutoff_date ? (
                          <Text fontSize="sm">
                            {new Date(quiz.cutoff_date).toLocaleDateString()}
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="gray.500">
                            No due date
                          </Text>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        {quiz.attempt_count > 0 ? (
                          <Tooltip
                            content={
                              quiz.last_attempt_date
                                ? `Last attempt: ${new Date(
                                    quiz.last_attempt_date,
                                  ).toLocaleDateString()}`
                                : "No completion date"
                            }
                          >
                            <Badge colorPalette="blue">
                              {quiz.attempt_count}{" "}
                              {quiz.attempt_count === 1
                                ? "attempt"
                                : "attempts"}
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Badge
                            colorPalette="orange"
                            variant="outline"
                            borderColor="orange.400"
                          >
                            Not attempted
                          </Badge>
                        )}
                      </Table.Cell>
                      <Table.Cell textAlign="center">
                        <Badge
                          colorPalette={quiz.allow_retake ? "green" : "gray"}
                        >
                          {quiz.allow_retake ? "Allowed" : "Not Allowed"}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>

            <Stack gap={4} display={{ base: "flex", md: "none" }}>
              {quizzes.map((quiz) => (
                <Box
                  key={quiz.id}
                  borderWidth={1}
                  borderRadius="lg"
                  p={4}
                  bg="white"
                  shadow="sm"
                  onClick={() =>
                    navigate({
                      to: "/quizzes/$quizId",
                      params: { quizId: quiz.id },
                    })
                  }
                  cursor="pointer"
                  borderLeftWidth={quiz.attempt_count === 0 ? "4px" : "1px"}
                  borderLeftColor={
                    quiz.attempt_count === 0 ? "orange.400" : undefined
                  }
                >
                  <Stack gap={2}>
                    <Flex justify="space-between" align="center" gap={3}>
                      <Text fontWeight="semibold" fontSize="lg">
                        {quiz.name}
                      </Text>
                      <Badge colorPalette="blue" variant="solid">
                        {quiz.question_count} Qs
                      </Badge>
                    </Flex>

                    <Flex gap={2} wrap="wrap">
                      {quiz.subjects.length > 0 ? (
                        quiz.subjects.map((subject) => (
                          <Badge
                            key={subject.id}
                            colorPalette="purple"
                            size="sm"
                          >
                            {subject.name}
                          </Badge>
                        ))
                      ) : (
                        <Text fontSize="sm" color="gray.500">
                          No subjects
                        </Text>
                      )}
                      {(quiz.tags ?? []).map((tag) => (
                        <Badge key={tag} colorPalette="blue" size="sm">
                          {tag}
                        </Badge>
                      ))}
                    </Flex>

                    <Flex gap={3} wrap="wrap">
                      <Badge colorPalette="gray" variant="outline">
                        {quiz.time_limit_minutes
                          ? `${quiz.time_limit_minutes} min`
                          : "No time limit"}
                      </Badge>
                      <Badge colorPalette="gray" variant="outline">
                        {quiz.cutoff_date
                          ? new Date(quiz.cutoff_date).toLocaleDateString()
                          : "No due date"}
                      </Badge>
                      {quiz.attempt_count > 0 ? (
                        <Tooltip
                          content={
                            quiz.last_attempt_date
                              ? `Last attempt: ${new Date(
                                  quiz.last_attempt_date,
                                ).toLocaleDateString()}`
                              : "No completion date"
                          }
                        >
                          <Badge colorPalette="blue" variant="solid">
                            {quiz.attempt_count}{" "}
                            {quiz.attempt_count === 1 ? "attempt" : "attempts"}
                          </Badge>
                        </Tooltip>
                      ) : (
                        <Badge
                          colorPalette="orange"
                          variant="outline"
                          borderColor="orange.400"
                        >
                          Not attempted
                        </Badge>
                      )}
                      <Badge
                        colorPalette={quiz.allow_retake ? "green" : "gray"}
                        variant="subtle"
                      >
                        {quiz.allow_retake ? "Retakes allowed" : "No retakes"}
                      </Badge>
                    </Flex>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </PageShell>
  )
}
